// Boot saga: pull rules once on app start, then every 5 minutes.
// The 5-minute cadence matches the chores cadence for sync polls;
// rule edits via PUT /api/v1/rules/{id} also push WS updates that
// re-fire predicateBoot via the WS plugin.

import { call, delay, put, takeLatest } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import { apiClient } from '@/infra/api/client';
import { SYNC_REFRESH } from '@/infra/sync/actions';
import { isTabHidden } from '@/infra/sync/visibility';

import {
  adaptAxios,
  loadRules,
  syncRules,
  type ElkaRule,
  type RuleSet,
} from './sync';
import { setLatestRules } from './rule-cache';

// Action types — kept inline rather than exported from a shared
// constants module so the predicate feature stays self-contained.
const PREDICATE_BOOT = 'predicate/boot';
const PREDICATE_RULES_LOADED = 'predicate/rulesLoaded';

export const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function predicateBoot() {
  return { type: PREDICATE_BOOT } as const;
}

export interface RulesLoadedAction {
  type: typeof PREDICATE_RULES_LOADED;
  payload: { rules: Record<string, unknown> };
}

export function rulesLoaded(rules: RuleSet): RulesLoadedAction {
  const payload: Record<string, unknown> = {};
  for (const [k, v] of rules) payload[k] = v;
  return { type: PREDICATE_RULES_LOADED, payload: { rules: payload } };
}

function* pullOnce(): SagaIterator {
  try {
    const client = adaptAxios(apiClient);
    const initial = loadRules();
    const next: RuleSet = yield call(syncRules, client, initial);
    yield put(rulesLoaded(next));
  } catch (e) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[predicate] pull failed', e);
    }
  }
}

export function* pollLoop(): SagaIterator {
  // First pull is fired by the boot action; this loop handles
  // subsequent ticks. We delay first so the boot pull doesn't run
  // twice.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    yield delay(POLL_INTERVAL_MS);
    // Skip the poll while the tab is hidden. Browsers throttle or
    // freeze background timers and in-flight fetches, so a snapshot
    // request fired now either trips the 10s axios timeout (background
    // throttling) or hangs until the tab wakes (OS/tab suspension) —
    // both surface as a misleading FAILED `http_call` flow in QPL even
    // though the server is healthy. Freshness is not lost: rule edits
    // re-fire predicateBoot via the WS plugin, and a catch-up pull runs
    // on the next sync cycle (the etag makes it a cheap not-modified
    // round-trip when nothing changed).
    if (isTabHidden()) continue;
    yield call(pollOnce);
  }
}

export function* predicateSaga(): SagaIterator {
  yield takeLatest(PREDICATE_BOOT, pullOnce);
  yield takeLatest(PREDICATE_RULES_LOADED, captureRulesRef);
  // Re-fire pullOnce when auth lands. The initial dispatch from
  // store/index.ts races foauth's silent refresh on cold start, so the
  // boot pull frequently 401s; the takeLatest below gives us a real
  // pull as soon as the user is authenticated.
  yield takeLatest('auth/loginSuccess', pullOnce);
  // Catch-up pull on every sync cycle. This covers both a WS reconnect
  // and a tab returning to the foreground after being hidden long
  // enough to have missed poll ticks — the coordinator collapses those
  // into one cycle and only dispatches once the tab is visible and the
  // server has actually answered a probe, which is what stops this pull
  // from being issued onto a dead socket. Pairs with the visibility
  // guard in pollLoop, which suppresses polling while hidden.
  yield takeLatest(SYNC_REFRESH, pullOnce);
  yield call(pollLoop);
}

// captureRulesRef mirrors the latest synced rule set into the
// shared rule cache that selectors read from.
function* captureRulesRef(action: RulesLoadedAction): SagaIterator {
  const map: RuleSet = new Map<string, ElkaRule>();
  for (const [k, v] of Object.entries(action.payload.rules)) {
    map.set(k, v as ElkaRule);
  }
  yield call(setLatestRules, map);
}

// pollOnce is an alias for pullOnce so the polling loop name
// matches the action it dispatches.
const pollOnce = pullOnce;
